/**
 * Route guard — if the user isn't signed in, show an email-first form.
 * The domain decides which IdP to route to (see authOptions.ts):
 *   - @artome.fi / @artomeusa.com → Microsoft (Entra) IdP
 *   - everything else            → Cognito-native password form
 *
 * No "Sign in with Microsoft" button is rendered to non-Artome users.
 * The email field acts as the gate.
 *
 * Returning users (auth-simplification / DE-182):
 *   - On cold load with a stored-but-expired session, we attempt ONE silent
 *     refresh-token renewal before showing any form — a returning user inside
 *     the 30-day refresh window never sees a login screen at all.
 *   - When no live session remains, we offer a one-click "Continue as <email>"
 *     using the remembered email, routed to the correct IdP.
 *   - Every re-auth path (including the error/timeout recovery that used to do
 *     a bare signinRedirect) is domain-aware, so a federated Artome user is
 *     never dumped on the Cognito-native password form (DE-182).
 */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useAuth } from 'react-oidc-context';

import { authMethodFor, recalledEmail, signinWith } from './authOptions';

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted">
      {children}
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const remembered = recalledEmail();
  const [email, setEmail] = useState(remembered ?? '');
  // Returning user → offer the one-click card first; unknown user → the form.
  const [showForm, setShowForm] = useState(!remembered);
  const [submitting, setSubmitting] = useState(false);
  const triedSilent = useRef(false);

  // Cold load with a stored-but-expired session (browser was closed past the
  // access-token lifetime but inside the 30-day refresh window): try a single
  // silent refresh-token renewal before rendering any login UI. On success the
  // user is authenticated with no interaction; on failure we fall through to
  // the gate below.
  useEffect(() => {
    if (triedSilent.current) return;
    if (auth.isLoading || auth.activeNavigator || auth.isAuthenticated) return;
    triedSilent.current = true;
    // oidc-client keeps the stored (expired) User until it's replaced, so a
    // refresh token is available here even though isAuthenticated is false.
    if (auth.user?.refresh_token) {
      void auth.signinSilent().catch(() => {
        /* refresh expired/revoked — show the gate */
      });
    }
  }, [auth]);

  // While loading, mid-redirect, or attempting the initial silent renewal,
  // don't flash a login form.
  const silentInFlight =
    !triedSilent.current && !auth.isAuthenticated && !!auth.user?.refresh_token;
  if (auth.isLoading || auth.activeNavigator || silentInFlight) {
    return <Centered>Loading…</Centered>;
  }

  // Domain-aware re-auth used by both the error card and the one-click button.
  // Prefer the (possibly expired) session's own email claim, then the
  // remembered email; if neither is known we can't route blindly to an IdP
  // (that's what stranded federated users on the native form — DE-182), so we
  // reveal the email-first form instead.
  const reauth = (hint?: string) => {
    const known = hint ?? auth.user?.profile?.email ?? remembered ?? '';
    if (!known.includes('@')) {
      setShowForm(true);
      return;
    }
    setSubmitting(true);
    void signinWith(authMethodFor(known), known, auth);
  };

  if (auth.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card max-w-md">
          <h2 className="font-semibold mb-2">Session expired</h2>
          <p className="text-sm text-muted">
            Your session timed out. Sign in again to continue.
          </p>
          <button
            className="mt-4 px-3 py-1.5 bg-accent text-white rounded-md text-sm disabled:opacity-60"
            disabled={submitting}
            onClick={() => reauth()}
          >
            {submitting ? 'Redirecting…' : 'Sign in'}
          </button>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    const onSubmit = (e: FormEvent) => {
      e.preventDefault();
      const trimmed = email.trim();
      if (!trimmed) return;
      setSubmitting(true);
      void signinWith(authMethodFor(trimmed), trimmed, auth);
    };

    // Returning user: one-click "Continue as <email>" (routed to the right
    // IdP), with an escape hatch to sign in as someone else.
    if (remembered && !showForm) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="card max-w-sm w-full text-center">
            <img
              src="/artome-logo.png"
              alt="Artome"
              className="mx-auto mb-4 h-10 w-auto"
            />
            <h1 className="text-xl font-semibold mb-1">DemoExp Portal</h1>
            <p className="text-sm text-muted mb-6">Welcome back</p>
            <button
              type="button"
              disabled={submitting}
              onClick={() => reauth(remembered)}
              className="w-full px-3 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Redirecting…' : `Continue as ${remembered}`}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-3 text-xs text-muted hover:text-fg underline"
            >
              Sign in as someone else
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <form onSubmit={onSubmit} className="card max-w-sm w-full text-center">
          {/* DE-152 — match the Hosted UI branding so the landing page
              doesn't read as a generic placeholder. Logo asset shared
              with Cognito Hosted UI (aws-setup/cognito/branding/). */}
          <img
            src="/artome-logo.png"
            alt="Artome"
            className="mx-auto mb-4 h-10 w-auto"
          />
          <h1 className="text-xl font-semibold mb-1">DemoExp Portal</h1>
          <p className="text-sm text-muted mb-6">Sign in to continue</p>
          <label className="block text-left mb-4">
            <span className="text-xs text-muted">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              placeholder="you@example.com"
              className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className="w-full px-3 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Redirecting…' : 'Continue'}
          </button>
        </form>
      </div>
    );
  }

  return <>{children}</>;
}
